import { isValidMimeType } from "@api";
import type { IDocument } from "@bbj-types/document";
import type {
  IUploadFileFieldErrorHandler,
  IUploadFileFieldOptions,
  TUploadFileFieldErrorHandlerChecker,
  TUploadFileFieldErrorHandlerMessageBuilder,
} from "../types";

const formatFilesNames = (files: IDocument[] | File[] | FileList) => {
  if (files.length < 2) {
    return files[0]?.name ?? "";
  }
  let filesNames = files[0].name;
  for (let i = 1; i < files.length - 1; i++) {
    filesNames += `, ${files[i].name}`;
  }
  filesNames += ` et ${files[files.length - 1].name}`;
  return filesNames;
};

const maxFileSizeChecker: TUploadFileFieldErrorHandlerChecker = (file, options) => {
  return !options.maxFileSize || file.size < options.maxFileSize;
};

const maxFileSizeMessageBuilder: TUploadFileFieldErrorHandlerMessageBuilder = (files, options) => {
  const maxFileSizeMo = Math.floor((options.maxFileSize ?? 0) / 1024 / 1024);
  if (files.length > 1) {
    return `Les documents "${formatFilesNames(files)}" n'ont pas pu être importés en raison de leur taille qui excède la limite de ${maxFileSizeMo}Mo.`;
  }
  return `Le document "${files[0].name}" n'a pas pu être importé en raison de sa taille qui excède la limite de ${maxFileSizeMo}Mo.`;
};

const mimeTypeChecker: TUploadFileFieldErrorHandlerChecker = (file) => {
  return isValidMimeType(file.type);
};

const mimeTypeMessageBuilder: TUploadFileFieldErrorHandlerMessageBuilder = (files) => {
  if (files.length > 1) {
    return `Les documents "${formatFilesNames(files)}" n'ont pas pu être importés en raison de leur format qui ne sont pas autorisé.`;
  }
  return `Le document "${files[0].name}" n'a pas pu être importé en raison de son format qui n'est pas autorisé.`;
};

export const maxFilesMessageBuilder = (files: IDocument[] | FileList, options: IUploadFileFieldOptions) => {
  const maxFiles = options.maxFiles ?? 0;
  if (files.length > 1) {
    return `Les documents "${formatFilesNames(files)}" n'ont pas pu être importés en raison de la limite de ${maxFiles} ${maxFiles > 1 ? "fichiers" : "fichier"} maximum`;
  }
  return `Le document "${files[0].name}" n'a pas pu être importé en raison de la limite de ${maxFiles} ${maxFiles > 1 ? "fichiers" : "fichier"} maximum`;
};

export const uploadErrorMessageBuilder = (files: File[]) => {
  if (files.length > 1) {
    return `Les documents "${formatFilesNames(files)}" n'ont pas pu être importés en raison d'une erreur lors de leur téléchargement.`;
  }
  return `Le document "${files[0].name}" n'a pas pu être importé en raison d'une erreur lors de son téléchargement.`;
};

const errorsHandlers = [
  { checker: maxFileSizeChecker, messageBuilder: maxFileSizeMessageBuilder },
  { checker: mimeTypeChecker, messageBuilder: mimeTypeMessageBuilder },
] as const satisfies IUploadFileFieldErrorHandler[];

export const handleErrors = (currentDocumentsCount: number, files: FileList, options: IUploadFileFieldOptions) => {
  const errors: string[] = [];
  const currentHandlers = errorsHandlers.map<IUploadFileFieldErrorHandler & { files: File[] }>((handler) => ({
    ...handler,
    files: [],
  }));
  if (options.maxFiles && currentDocumentsCount + files.length > options.maxFiles) {
    return [maxFilesMessageBuilder(files, options)];
  }
  for (const file of files) {
    for (const handler of currentHandlers) {
      if (!handler.checker(file, options)) {
        handler.files.push(file);
      }
    }
  }
  for (const handler of currentHandlers) {
    if (handler.files.length > 0) {
      errors.push(handler.messageBuilder(handler.files, options));
    }
  }
  return errors;
};
